import { QuestionIcon } from '@phosphor-icons/react'
import React from 'react'
import { type Tool } from '../utils/api-client/api-schemas'
import { isScaTool } from '../utils/detector-utils'
import { formatPercentage } from '../utils/text-utils'
import * as styles from './analysis-metrics.css'
import { TooltipWithHover } from './tooltip'

type Metric = { count: number; total: number }
type AnalysisMetricsProps = {
  tool: Tool
  triage: Metric
  fix?: Metric
  sca?: Metric
}

export const AnalysisMetrics: React.FC<AnalysisMetricsProps> = ({ tool, triage, fix, sca }) => {
  if (isScaTool(tool)) {
    return (
      <div className={styles.metricsContainer}>
        <div className={styles.compositionRow}>
          <ScaMetric metric={sca} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.metricsContainer}>
      <div className={styles.compositionRow}>
        <TriageMetric metric={triage} />
      </div>
      {fix && fix.total > 0 && (
        <div className={styles.compositionRow}>
          <FixMetric metric={fix} />
        </div>
      )}
    </div>
  )
}

const TriageMetric: React.FC<{ metric: Metric }> = ({ metric }) => (
  <div className={styles.metricContainer}>
    <span className={styles.metricLabel}>Triage coverage:</span>
    <output aria-label="Triage coverage" className={styles.metricValue}>
      {formatPercentage(metric.count / metric.total)}
    </output>
  </div>
)

const FixMetric: React.FC<{ metric?: Metric }> = ({ metric }) => (
  <div className={styles.metricContainer}>
    <span className={styles.metricLabel}>Fix coverage:</span>
    <output aria-label="Fix coverage" className={styles.metricValue}>
      {metric ? formatPercentage(metric.count / metric.total) : '-'}
    </output>
    <TooltipWithHover
      trigger={<QuestionIcon className={styles.questionMarkIcon} size={16} weight="regular" />}
      side="right"
      maxWidth={400}
    >
      <span>
        Note that Pixee aims to reflect <em>meaningful</em> fix coverage with this metric, and therefore{' '}
        <em>excludes</em> any findings that have been triaged as False Positive or Won't Fix from the calculation.
      </span>
    </TooltipWithHover>
  </div>
)

const ScaMetric: React.FC<{ metric?: Metric }> = ({ metric }) => (
  <div className={styles.metricContainer}>
    <span className={styles.metricLabel}>Coverage:</span>
    <output aria-label="Coverage" className={styles.metricValue}>
      {metric ? formatPercentage(metric.count / metric.total) : '-'}
    </output>
  </div>
)
